import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { Mail, Loader2, CheckCircle2, ArrowLeft, AlertCircle } from 'lucide-react';
import logo from '../assets/LOGO_AZUL_SIN_FONDO.png';

const ForgotPassword = () => {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Precarga del usuario cuando venimos del signup con "ya tienes cuenta" (?u=...).
  useEffect(() => {
    const u = new URLSearchParams(window.location.search).get('u');
    if (u) setIdentifier(u);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await axiosClient.post('/reset-password', { identifier: identifier.trim().toLowerCase() });
      setResult({
        masked_phone: data?.masked_phone || null,
        masked_email: data?.masked_email || null,
      });
    } catch (err) {
      setError('Ha ocurrido un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-gradient-to-br from-gray-50 via-white to-blue-50 flex p-4 sm:p-6 relative">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
      </div>

      {/* Card */}
      <div className="relative bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 p-6 sm:p-10 w-full max-w-md m-auto transition-all duration-300 hover:shadow-blue-100/30">
        {/* Logo */}
        <div className="text-center mb-10">
          <img
            src={logo}
            alt="ConextCRM"
            className="w-48 sm:w-56 h-auto max-w-full mx-auto mb-6 drop-shadow-sm"
          />
          <p className="font-bold text-gray-800">Recuperar contraseña</p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 text-red-600 rounded-xl">
                <AlertCircle size={20} />
              </div>
              <p className="text-sm font-bold text-red-800">{error}</p>
            </div>
          </div>
        )}

        {result === null ? (
          /* Form state */
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-bold uppercase tracking-widest text-[11px] text-gray-400 mb-2.5 ml-1">
                Usuario o email
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                </div>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 text-base bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-700 placeholder:text-gray-300"
                  placeholder="Tu móvil, usuario o email"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-200/50 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  <span>Enviando...</span>
                </>
              ) : (
                <span>Enviar contraseña temporal</span>
              )}
            </button>
          </form>
        ) : (
          /* Result state */
          <div className="mb-8 p-4 bg-blue-50 border border-blue-100 rounded-2xl animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl shrink-0">
                <CheckCircle2 size={20} />
              </div>
              <div className="space-y-4">
                <p className="text-sm font-bold text-blue-800">
                  {result.masked_phone
                    ? `Si el usuario existe y tiene teléfono registrado, recibirás un WhatsApp en ${result.masked_phone} con tu contraseña temporal.`
                    : result.masked_email
                    ? `Te hemos enviado una contraseña temporal por email a ${result.masked_email}. Revisa también el spam.`
                    : 'Si el usuario existe y tiene teléfono registrado, recibirás un WhatsApp con tu contraseña temporal en breve.'}
                </p>
                <a
                  href="mailto:info@conextcrm.es"
                  className="inline-block text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors underline underline-offset-2"
                >
                  {result.masked_email && !result.masked_phone
                    ? "¿Sin acceso a ese correo? Contacta con soporte"
                    : "¿Sin acceso al número? Contacta con soporte"}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-8 border-t border-gray-50 flex flex-col items-center gap-4">
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft size={16} />
            Volver al inicio de sesión
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
